import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useI18n } from '../i18n';
import { api } from '../api/client';
import { formatTime } from '../lib/utils';

/**
 * API 访问令牌管理卡片（设置页）
 *
 * - 列出令牌（名称 / 创建时间 / 最后使用 / 撤销）
 * - 创建令牌：明文仅在创建后显示一次，带复制按钮
 * - 撤销需确认，撤销后使用该令牌的工具立即失去访问权限
 */
export default function TokenManager() {
  const { t, lang } = useI18n();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const { data: tokens, isLoading } = useQuery({
    queryKey: ['tokens'],
    queryFn: () => api.getApiTokens(),
  });

  const createMutation = useMutation({
    mutationFn: () => api.createApiToken(name.trim() || 'Token'),
    onSuccess: (data) => {
      setCreatedToken(data.token);
      setCopied(false);
      setCreateError(null);
      setName('');
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
    },
    onError: (err) => {
      setCreateError(err instanceof Error ? err.message : String(err));
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (tokenId: string) => api.revokeApiToken(tokenId),
    onSuccess: () => {
      setRevokingId(null);
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
    },
  });

  async function copyToken() {
    if (!createdToken) return;
    try {
      await navigator.clipboard.writeText(createdToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // 剪贴板不可用时降级为全选提示
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">{t('settings.tokens.title')}</span>
      </div>
      <p style={{ fontSize: '0.85rem', color: 'var(--ink2)', marginBottom: '1rem', lineHeight: 1.6 }}>
        {t('settings.tokens.intro')}
      </p>

      {/* 创建成功：明文仅显示一次 */}
      {createdToken && (
        <div
          className="state-box"
          style={{
            borderColor: 'var(--accent)',
            background: 'rgba(99,102,241,0.06)',
            marginBottom: '1rem',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: '0.5rem',
          }}
        >
          <span style={{ fontSize: '0.85rem', color: 'var(--ink2)' }}>
            {t('settings.tokens.onceHint')}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <code
              style={{
                flex: 1,
                fontFamily: 'var(--font-mono)',
                fontSize: '0.78rem',
                wordBreak: 'break-all',
                padding: '0.5rem 0.75rem',
                background: 'var(--bg2, rgba(0,0,0,0.04))',
                borderRadius: '6px',
              }}
            >
              {createdToken}
            </code>
            <button className="btn btn-ghost" onClick={copyToken} style={{ whiteSpace: 'nowrap' }}>
              {copied ? t('settings.tokens.copied') : t('settings.tokens.copy')}
            </button>
          </div>
        </div>
      )}

      {/* 创建表单 */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          className="input"
          style={{ flex: 1, minWidth: '200px' }}
          placeholder={t('settings.tokens.namePlaceholder')}
          aria-label={t('settings.tokens.nameLabel')}
          value={name}
          maxLength={50}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !createMutation.isPending) createMutation.mutate();
          }}
        />
        <button
          className="btn btn-accent"
          onClick={() => createMutation.mutate()}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending ? t('settings.tokens.creating') : t('settings.tokens.create')}
        </button>
      </div>
      {createError && (
        <div className="field-error" style={{ marginBottom: '0.75rem' }}>
          {createError}
        </div>
      )}

      {/* 令牌列表 */}
      {isLoading ? (
        <div className="state-box">{t('common.loading')}</div>
      ) : !tokens || tokens.length === 0 ? (
        <div className="state-box">{t('settings.tokens.empty')}</div>
      ) : (
        <dl className="info-list">
          {tokens.map((token) => (
            <div
              key={token.id}
              className="info-row"
              style={{ flexWrap: 'wrap', gap: '0.25rem' }}
            >
              <dt style={{ minWidth: 'auto' }}>{token.name}</dt>
              <dd style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', fontSize: '0.78rem' }}>
                <span>
                  {t('settings.tokens.createdAt')}: {formatTime(token.createdAt, lang)}
                </span>
                <span>
                  {t('settings.tokens.lastUsed')}:{' '}
                  {token.lastUsedAt ? formatTime(token.lastUsedAt, lang) : t('settings.tokens.never')}
                </span>
                {revokingId === token.id ? (
                  <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ color: 'var(--muted)' }}>{t('settings.tokens.revokeConfirm')}</span>
                    <button
                      className="btn btn-ghost"
                      onClick={() => setRevokingId(null)}
                      disabled={revokeMutation.isPending}
                    >
                      {t('billing.keepSub')}
                    </button>
                    <button
                      className="btn"
                      style={{ background: 'var(--warn)', color: '#fff', padding: '0.25rem 0.75rem', fontSize: '0.78rem' }}
                      onClick={() => revokeMutation.mutate(token.id)}
                      disabled={revokeMutation.isPending}
                    >
                      {revokeMutation.isPending ? t('settings.tokens.revoking') : t('billing.confirmCancel')}
                    </button>
                  </span>
                ) : (
                  <button
                    className="btn btn-ghost"
                    style={{ color: 'var(--warn)', padding: '0.25rem 0.75rem', fontSize: '0.78rem' }}
                    onClick={() => setRevokingId(token.id)}
                  >
                    {t('settings.tokens.revoke')}
                  </button>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
